/**
 * 應付沖帳(REQ-20260806002)。
 *
 * 需求:「進貨/外包確認後,自動產生對應供應商的應付帳款明細。」
 * 進貨單一過帳就會出現在這裡 —— 不需要額外的「產生應付」動作,
 * 因為未結應付是由進貨單與沖帳紀錄直接算出來的,不是另一份被複製的資料。
 *
 * REQ-20260815002 補上「登錄付款」——「看得到欠多少」與「付了錢能登錄」
 * 在應收側是同一個畫面的兩半,應付側原本只有前半。
 *
 * REQ-20260831001 補上**廠商對帳單**:未結明細只回答「現在還欠多少」,
 * 要查印「某段期間跟這家廠商往來了什麼」得有期間 + 品項 + 滾動餘額 ——
 * 那正是應收側月結對帳單在做的事,應付側原本完全沒有。
 */
import { useEffect, useRef, useState } from 'react';
import { type PartyInfo } from '@app/shared';
import { ExportJpegButton } from '../../components/ExportJpegButton';
import { PartyPicker } from '../../components/PartyPicker';
import { buildDocumentFilename } from '../../lib/filename';
import { formatMoney } from '../../lib/format-money';
import { referenceNameMap, useReferences } from '../settings/api';
import { useApOutstanding, useSupplierStatement } from '../accounting/api';
import { PaymentModal } from './PaymentModal';
import { SupplierStatementView } from './SupplierStatementView';

export function PayablePanel(): JSX.Element {
  const [supplier, setSupplier] = useState<PartyInfo | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  /**
   * 對帳期間(REQ-20260831001)。結構與預設值完全比照 `ReceivablePanel` ——
   * 應收 / 應付是同一件事的兩面,兩頁的操作方式不該長得不一樣。
   */
  const [period, setPeriod] = useState(currentPeriodInTaipei());
  const [customRange, setCustomRange] = useState(false);
  const [from, setFrom] = useState(() => `${currentPeriodInTaipei()}-01`);
  const [to, setTo] = useState(todayInTaipei);
  const { data, isLoading, isError, error } = useApOutstanding(supplier?.id);
  const statement = useSupplierStatement(supplier?.id, customRange ? { from, to } : { period });
  /**
   * 進貨類別的 code → 中文(REQ-20260814002)。與進貨管理列表同一份清單與同一組參數
   * (`all=true`,列的是歷史單,類別可能已被停用),兩邊共用 React Query 的快取。
   */
  const { data: purchaseCategories } = useReferences('purchase-categories', true);
  const categoryNames = referenceNameMap(purchaseCategories?.data);
  const rows = data ?? [];
  const totalOutstanding = rows.reduce((sum, o) => sum + Number(o.outstanding), 0);
  /**
   * 有溢付時才出現的說明(REQ-20261029002)。使用者看到 4 張已付約 2 倍、未結為負的
   * mdb 舊單,問「看不懂,怎麼沖帳」—— 負數是刻意保留的(REQ-20260815002),
   * 但畫面上沒有任何一句話說它代表什麼、該去哪裡處理。
   */
  const hasOverpaid = totalOutstanding < 0 || rows.some((o) => Number(o.outstanding) < 0);

  /*
   * 存檔預設檔名(REQ-20260903006)。與應收側對稱,做法完全相同:
   * 設 `document.title`(REQ-20260811002 那一招),cleanup 還原成進來之前的值。
   * 日期段落一樣去掉連字號,免得與段落分隔符混在一起。
   */
  const periodSegment = customRange
    ? `${from.replaceAll('-', '')}-${to.replaceAll('-', '')}`
    : period.replaceAll('-', '');
  const documentFilename = buildDocumentFilename(['對帳單', supplier?.simpleName, periodSegment]);
  useEffect(() => {
    const previousTitle = document.title;
    document.title = documentFilename;
    return () => {
      document.title = previousTitle;
    };
  }, [documentFilename]);

  /** 「另存 JPEG」要截的那一塊:廠商對帳單的 `.print-area` */
  const sheetRef = useRef<HTMLDivElement>(null);

  return (
    <div className="grid gap-4">
      <div className="grid gap-4 md:grid-cols-2 no-print">
        <PartyPicker type="supplier" value={supplier} onChange={setSupplier} />
        <div className="grid gap-1">
          <div className="flex items-center justify-between gap-2">
            <label
              className="text-sm text-slate-600"
              htmlFor={customRange ? 'ap-stmt-from' : 'ap-stmt-period'}
            >
              對帳期間
            </label>
            <label className="flex items-center gap-1 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={customRange}
                onChange={(e) => setCustomRange(e.target.checked)}
                data-testid="ap-stmt-custom-range"
              />
              自訂區間
            </label>
          </div>
          {customRange ? (
            <div className="flex items-center gap-1">
              <input
                id="ap-stmt-from"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                aria-label="起日"
                className="border rounded-md px-2 py-2 text-sm"
              />
              <span className="text-slate-400">~</span>
              <input
                id="ap-stmt-to"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                aria-label="迄日"
                className="border rounded-md px-2 py-2 text-sm"
              />
            </div>
          ) : (
            <input
              id="ap-stmt-period"
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
            />
          )}
        </div>
      </div>

      {supplier ? (
        <div className="flex items-center justify-between no-print">
          <h3 className="font-medium text-slate-700">未結應付明細</h3>
          <button
            type="button"
            data-testid="payment-open"
            onClick={() => setPaymentOpen(true)}
            disabled={rows.length === 0}
            className="bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            登錄付款
          </button>
        </div>
      ) : null}

      {!supplier ? (
        <div className="text-slate-500 text-sm no-print">
          先選一個廠商,就會列出他的未結應付明細與廠商對帳單。
        </div>
      ) : isLoading ? (
        <div role="status" className="text-slate-500 text-sm no-print">
          載入中...
        </div>
      ) : isError ? (
        <div role="alert" className="text-red-600 text-sm no-print">
          載入失敗：{error instanceof Error ? error.message : '未知錯誤'}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-slate-500 text-sm no-print">
          這個廠商目前沒有未結的應付款項。到「進貨管理」建單並過帳後就會出現。
        </div>
      ) : (
        <>
          <table className="w-full bg-white shadow-sm rounded-md no-print">
            <thead>
              <tr className="border-b text-left text-sm text-slate-600">
                <th className="px-4 py-2">日期</th>
                <th className="px-4 py-2">單號</th>
                <th className="px-4 py-2">類別</th>
                <th className="px-4 py-2">代管客戶</th>
                <th className="px-4 py-2 text-right">含稅總計</th>
                <th className="px-4 py-2 text-right">已付</th>
                <th className="px-4 py-2 text-right">折讓</th>
                <th className="px-4 py-2 text-right">未結</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-b last:border-0 hover:bg-slate-50 text-sm">
                  <td className="px-4 py-2 whitespace-nowrap">{o.txnDate}</td>
                  <td className="px-4 py-2 whitespace-nowrap font-mono">{o.orderNo}</td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {categoryNames[o.orderType] ?? o.orderType}
                  </td>
                  <td className="px-4 py-2 max-w-xs truncate">{o.customerSimpleName ?? '—'}</td>
                  <td className="px-4 py-2 text-right font-mono">{formatMoney(o.total)}</td>
                  <td className="px-4 py-2 text-right font-mono">{formatMoney(o.settledAmount)}</td>
                  <td className="px-4 py-2 text-right font-mono">
                    {formatMoney(o.allowanceAmount)}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{formatMoney(o.outstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-sm text-slate-600 text-right no-print">
            未結應付合計{' '}
            <span className="font-mono font-medium">{formatMoney(totalOutstanding)}</span>
          </p>
          {hasOverpaid ? (
            <p className="text-xs text-slate-500 text-right no-print" data-testid="ap-overpaid-hint">
              未結為負數代表溢付(已付超過應付)。如果是重複登錄的付款,請到廠商頁『應付沖帳』對那筆付款按『解除沖銷』。
            </p>
          ) : null}
        </>
      )}

      {/*
        廠商對帳單(REQ-20260831001)。接在未結明細下方,與應收沖帳頁對稱 ——
        上半是「哪幾張單還沒付」,下半是可以列印 / 寄給廠商核對的對帳單。
        列印時只留這一塊(其餘掛 `no-print`),沿用同一組 `.print-area` 樣式。
      */}
      {supplier ? (
        <section className="grid gap-2" ref={sheetRef}>
          <div className="flex items-center justify-between no-print">
            <h3 className="font-medium text-slate-700">廠商對帳單</h3>
            <div className="flex items-center gap-3">
              {/* 同一份對帳單的第二種存檔格式(REQ-20260903006);PDF 那條路一行未動 */}
              <ExportJpegButton
                targetRef={sheetRef}
                filename={documentFilename}
                disabled={!statement.data}
              />
              <button
                type="button"
                onClick={() => window.print()}
                disabled={!statement.data}
                className="border border-blue-600 text-blue-700 px-3 py-1.5 rounded hover:bg-blue-50 disabled:opacity-50"
              >
                列印 / 匯出 PDF
              </button>
            </div>
          </div>
          {statement.isLoading ? (
            <div role="status" className="text-slate-500 text-sm">
              載入中...
            </div>
          ) : statement.isError ? (
            <div role="alert" className="text-red-600 text-sm">
              載入失敗：
              {statement.error instanceof Error ? statement.error.message : '未知錯誤'}
            </div>
          ) : statement.data ? (
            <SupplierStatementView statement={statement.data} />
          ) : null}
        </section>
      ) : null}

      {supplier ? (
        <PaymentModal
          open={paymentOpen}
          onClose={() => setPaymentOpen(false)}
          supplierId={supplier.id}
          outstanding={rows}
        />
      ) : null}
    </div>
  );
}

/**
 * 預設對帳期間 = 台北時區的當月。**不用 `new Date()` 的本地日期** ——
 * 瀏覽器時區不見得是台北,月初 / 月底會差一整期(CLAUDE.md §6.1 的前端版)。
 */
function currentPeriodInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
  })
    .format(new Date())
    .slice(0, 7);
}

/** 自訂區間的預設迄日 = 台北今天 */
function todayInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
